import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { AlertCircle, AlertTriangle, Check, Loader2, Phone, Search, Users } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useZaloAccounts } from '@/hooks/use-integrations'
import { useContacts } from '@/hooks/use-contacts'
import {
  usePreviewStartConversation,
  useStartConversation,
  type StartConversationMode,
  type StartPreview,
} from '@/hooks/use-conversations'
import { ChannelIcon } from '@/pages/integrations/channel-catalog'
import { apiError } from '@/lib/api-client'
import { cn, initials } from '@/lib/utils'
import { buildChannelOptions, isPlausiblePhone, phoneOnlyHint } from './new-conversation-lib'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Mở hội thoại vừa tạo / sẵn có. */
  onOpened: (conversationId: string) => void
}

const SESSION_PRIVATE = 'private'

/** Nhãn bắt buộc có dấu * đỏ như form SaleSmartly. */
function FieldLabel({ htmlFor, children, required }: { htmlFor?: string; children: React.ReactNode; required?: boolean }) {
  return (
    <Label htmlFor={htmlFor} className="text-[13px] font-medium">
      {required && <span className="mr-0.5 text-destructive">*</span>}
      {children}
    </Label>
  )
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

/**
 * "Bắt đầu cuộc trò chuyện mới" — chọn kênh, loại phiên, tài khoản rồi nhập SĐT
 * (Zalo cá nhân) hoặc chọn khách hàng có sẵn. Tham khảo modal của SaleSmartly.
 */
export function NewConversationDialog({ open, onOpenChange, onOpened }: Props) {
  const { t } = useTranslation('convContact')
  const { data: allAccounts, isLoading: loadingAccounts } = useZaloAccounts()
  const channels = useMemo(() => buildChannelOptions(allAccounts ?? []), [allAccounts])

  const [channelKey, setChannelKey] = useState('')
  const [accountId, setAccountId] = useState('')
  const [mode, setMode] = useState<StartConversationMode>('phone')
  const [phone, setPhone] = useState('')
  const [preview, setPreview] = useState<StartPreview | null>(null)
  const [contactId, setContactId] = useState('')

  const channel = channels.find((c) => c.def.key === channelKey) ?? null
  const phoneAllowed = !!channel?.phoneSupported

  const previewMut = usePreviewStartConversation()
  const startMut = useStartConversation()

  // Mặc định: kênh đầu tiên (kênh hỗ trợ SĐT xếp trước) + tài khoản đầu tiên.
  useEffect(() => {
    if (!open || channelKey || channels.length === 0) return
    setChannelKey(channels[0].def.key)
  }, [open, channelKey, channels])

  useEffect(() => {
    if (!channel) return
    if (!channel.accounts.some((a) => a.id === accountId)) setAccountId(channel.accounts[0]?.id ?? '')
    if (!channel.phoneSupported) setMode('contact')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel?.def.key, channel?.accounts])

  // Đổi kênh / tài khoản / số → kết quả tìm cũ không còn đúng.
  useEffect(() => { setPreview(null) }, [accountId, phone, mode])

  // Đóng → dọn sạch.
  useEffect(() => {
    if (open) return
    setChannelKey('')
    setAccountId('')
    setMode('phone')
    setPhone('')
    setPreview(null)
    setContactId('')
    previewMut.reset()
    startMut.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const canSearchPhone = phoneAllowed && !!accountId && isPlausiblePhone(phone) && !previewMut.isPending
  const canCreate =
    !!accountId &&
    !startMut.isPending &&
    (mode === 'phone' ? phoneAllowed && !!preview : !!contactId)

  function handleSearchPhone() {
    if (!canSearchPhone) return
    previewMut.mutate(
      { accountId, mode: 'phone', phone: phone.trim() },
      {
        onSuccess: (p) => setPreview(p),
        onError: (e) => {
          setPreview(null)
          toast.error(apiError(e) || t('newConv.searchFailed'))
        },
      },
    )
  }

  function handleCreate() {
    if (!canCreate) return
    startMut.mutate(
      mode === 'phone'
        ? { accountId, mode: 'phone', phone: phone.trim() }
        : { accountId, mode: 'contact', contactId },
      {
        onSuccess: (r) => {
          toast.success(r.created ? t('newConv.createdToast') : t('newConv.openedToast'))
          onOpenChange(false)
          onOpened(r.conversationId)
        },
        onError: (e) => toast.error(apiError(e) || t('newConv.createFailed')),
      },
    )
  }

  const channelName = channel?.def.name ?? t('newConv.channelFallback')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('newConv.title')}</DialogTitle>
          <DialogDescription>{t('newConv.description')}</DialogDescription>
        </DialogHeader>

        {loadingAccounts ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> {t('newConv.loadingChannels')}
          </div>
        ) : channels.length === 0 ? (
          <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <p>
              <Trans
                t={t}
                i18nKey="newConv.noChannels"
                components={{
                  link: <Link to="/integrations" className="font-medium text-primary hover:underline" onClick={() => onOpenChange(false)} />,
                }}
              />
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* 1. Kênh */}
            <div className="space-y-1.5">
              <FieldLabel htmlFor="nc-channel" required>{t('newConv.channelLabel')}</FieldLabel>
              <Select value={channelKey} onValueChange={setChannelKey}>
                <SelectTrigger id="nc-channel">
                  <SelectValue placeholder={t('newConv.channelPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {channels.map((c) => (
                    <SelectItem key={c.def.key} value={c.def.key}>
                      <span className="flex items-center gap-2">
                        <ChannelIcon def={c.def} size="sm" />
                        <span>{c.def.name}</span>
                        {!c.phoneSupported && (
                          <span className="text-xs text-muted-foreground">{t('newConv.existingOnly')}</span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* 2. Loại phiên */}
            <div className="space-y-1.5">
              <FieldLabel htmlFor="nc-type" required>{t('newConv.sessionType')}</FieldLabel>
              <Select value={SESSION_PRIVATE} disabled>
                <SelectTrigger id="nc-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SESSION_PRIVATE}>{t('newConv.sessionPrivate')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* 3. Tài khoản */}
            <div className="space-y-1.5">
              <FieldLabel htmlFor="nc-account" required>{t('newConv.accountLabel', { channel: channelName })}</FieldLabel>
              {channel && channel.accounts.length === 0 ? (
                <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                  <p>{t('newConv.noAccounts', { channel: channelName })}</p>
                </div>
              ) : (
                <Select value={accountId} onValueChange={setAccountId}>
                  <SelectTrigger id="nc-account">
                    <SelectValue placeholder={t('newConv.accountPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {channel?.accounts.map((a) => {
                      const name = a.displayName || a.phone || t('common:fields.account')
                      return (
                        <SelectItem key={a.id} value={a.id}>
                          <span className="flex items-center gap-2">
                            <Avatar className="h-5 w-5">
                              {a.avatarUrl && <AvatarImage src={a.avatarUrl} alt="" />}
                              <AvatarFallback className="text-[9px]">{initials(name)}</AvatarFallback>
                            </Avatar>
                            <span className="truncate">{name}</span>
                          </span>
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* 4. Người nhận: SĐT | Khách hàng có sẵn */}
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-1 rounded-md bg-secondary p-0.5" role="tablist">
                {([
                  { value: 'phone', label: t('newConv.tabPhone'), icon: Phone, disabled: !phoneAllowed },
                  { value: 'contact', label: t('newConv.tabContact'), icon: Users, disabled: false },
                ] as const).map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    role="tab"
                    aria-selected={mode === tab.value}
                    disabled={tab.disabled}
                    title={tab.disabled ? phoneOnlyHint() : undefined}
                    onClick={() => setMode(tab.value)}
                    className={cn(
                      'flex h-7 items-center justify-center gap-1.5 rounded-[5px] text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                      mode === tab.value ? 'bg-card font-medium text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <tab.icon className="h-3.5 w-3.5" />
                    {tab.label}
                  </button>
                ))}
              </div>

              {!phoneAllowed && (
                <p className="text-xs text-muted-foreground">
                  {t('newConv.phoneOnlyNote', { hint: phoneOnlyHint() })}
                </p>
              )}

              {mode === 'phone' && phoneAllowed && (
                <div className="space-y-2">
                  <FieldLabel htmlFor="nc-phone">{t('newConv.phoneLabel')}</FieldLabel>
                  <div className="flex gap-2">
                    <Input
                      id="nc-phone"
                      inputMode="tel"
                      autoComplete="off"
                      placeholder={t('newConv.phonePlaceholder')}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleSearchPhone()
                        }
                      }}
                    />
                    <Button type="button" variant="outline" onClick={handleSearchPhone} disabled={!canSearchPhone}>
                      {previewMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                      {t('newConv.search')}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('newConv.phoneHelp', { channel: channelName })}
                  </p>
                  <div className="flex items-start gap-2 rounded-md border border-orange-300/60 bg-orange-50 p-2.5 text-xs text-orange-800 dark:border-orange-500/30 dark:bg-orange-500/10 dark:text-orange-300">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <p>
                      {t('newConv.zaloWarning')}
                    </p>
                  </div>

                  {preview && (
                    <div className="flex items-center gap-3 rounded-lg border bg-card p-3">
                      <Avatar className="h-10 w-10">
                        {preview.profile.avatar && <AvatarImage src={preview.profile.avatar} alt="" />}
                        <AvatarFallback>{initials(preview.profile.name)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{preview.profile.name}</p>
                        <p className="truncate text-xs text-muted-foreground">UID: {preview.profile.uid}</p>
                      </div>
                      {preview.existingConversationId ? (
                        <Badge variant="secondary">{t('newConv.hasConversation')}</Badge>
                      ) : (
                        <Check className="h-4 w-4 text-success" />
                      )}
                    </div>
                  )}
                </div>
              )}

              {mode === 'contact' && <ContactPicker value={contactId} onChange={setContactId} />}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="button" onClick={handleCreate} disabled={!canCreate}>
            {startMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === 'phone' && preview?.existingConversationId ? t('newConv.openConversation') : t('newConv.createSession')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Chọn khách hàng có sẵn (chỉ gắn khi đang ở tab này → không gọi API thừa). */
function ContactPicker({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const { t } = useTranslation('convContact')
  const [contactSearch, setContactSearch] = useState('')
  const debouncedSearch = useDebounced(contactSearch.trim(), 300)
  const contactsQ = useContacts({ search: debouncedSearch || undefined, limit: 8 })
  const contacts = contactsQ.data?.contacts ?? []
  const contactId = value
  const setContactId = onChange

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-8"
          placeholder={t('newConv.contactSearchPlaceholder')}
          value={contactSearch}
          onChange={(e) => setContactSearch(e.target.value)}
        />
      </div>
      <div className="max-h-52 space-y-0.5 overflow-y-auto rounded-md border p-1">
        {contactsQ.isLoading ? (
          <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> {t('common:status.loading')}
          </div>
        ) : contacts.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t('newConv.noContacts')}</p>
        ) : (
          contacts.map((c) => {
            const name = c.crmName || c.fullName || t('newConv.noName')
            const active = c.id === contactId
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setContactId(c.id)}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded px-2 py-1.5 text-left transition-colors',
                  active ? 'bg-primary/10' : 'hover:bg-muted',
                )}
              >
                <Avatar className="h-7 w-7">
                  {c.avatarUrl && <AvatarImage src={c.avatarUrl} alt="" />}
                  <AvatarFallback className="text-[10px]">{initials(name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className={cn('truncate text-sm', active && 'font-medium text-primary')}>{name}</p>
                  <p className="truncate text-xs text-muted-foreground">{c.phone || c.email || '—'}</p>
                </div>
                {active && <Check className="h-4 w-4 shrink-0 text-primary" />}
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}
